import { useCallback, useEffect, useMemo, useRef } from 'react';
import { QueryObserverResult, RefetchOptions, RefetchQueryFilters } from 'react-query';

import { OrganizationPermissionsDTO, useGetOrganizationPermissions } from '@/api/generated';
import { useLocation } from '@/hooks/entities/useLocation';
import { getErrorStatus } from '@/utils/error';

import { PermissionContextVariables } from './usePermissions';

type UseOrganizationPermissionsResult = {
  value?: OrganizationPermissionsDTO;
  error: unknown;
  isLoading: boolean;
  isAccessDenied: boolean;
  refetch: <TPageData>(
    options?: RefetchOptions & RefetchQueryFilters<TPageData>
  ) => Promise<QueryObserverResult<OrganizationPermissionsDTO, unknown>> | undefined;
};

export const useOrganizationPermissions = ({
  variables
}: {
  variables?: PermissionContextVariables;
} = {}): UseOrganizationPermissionsResult => {
  const location = useLocation();

  const organizationSlug = variables?.organizationSlug ?? location.organizationSlug;

  const {
    data: organizationPermissions,
    error,
    isLoading,
    refetch
  } = useGetOrganizationPermissions(organizationSlug!, {
    query: { enabled: !!organizationSlug }
  });

  const isAccessDenied = useMemo(() => [401, 403, 404].includes(getErrorStatus(error) ?? 0), [error]);

  const canReturnValue = !!organizationSlug && !isAccessDenied;

  const canRefetchValueRef = useRef(canReturnValue);

  useEffect(() => {
    canRefetchValueRef.current = canReturnValue;
  }, [canReturnValue]);

  const refetchWithCanReturnValue = useCallback(
    <TPageData>(options?: RefetchOptions & RefetchQueryFilters<TPageData>) => {
      if (canRefetchValueRef.current) {
        return refetch(options);
      }
    },
    [refetch]
  );

  return {
    value: canReturnValue ? organizationPermissions || {} : undefined,
    error,
    isLoading,
    isAccessDenied,
    refetch: refetchWithCanReturnValue
  };
};
